/* @flow */

import * as React from 'react';
import styled from 'styled-components';
import FlexBox, { BorderedFlexBox } from '../FlexBox';
import Tooltip from '../Tooltip';
import InnerOrderForms from './InnerOrderForms';
import Fee from './Fee';
import zIndexScale from '../../utils/zIndexScale';
import urlConfig from '../../config/urls';
import { getSupportLocale } from '../../utils/locales';
import { getLabel } from '../../utils/i18n';
import TradeRuleTip from '../TradeRule/TradeRuleTip';

const Wrapper = styled(FlexBox)`
  margin: 4px 5px 0px;
  min-height: 274px;
  width: 100%;
`;

const Tabs = styled.ul`
  display: flex;
  padding-left: 0;
  padding-bottom: 0;
  position: relative;
`;

const Tab = styled.li`
  align-items: baseline;
  background: ${({ theme, selected }) => selected ? theme.formsbg : theme.buttonbg};
  border: 1px solid ${({ theme }) => theme.border || '#eee'};
  border-bottom-color: ${({ theme, selected }) => selected ? theme.formsbg : theme.border || '#eee'};
  box-sizing: content-box;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  padding: 3px 14px;
  margin: 0;
  user-select: none;
  height: 22px;
  line-height: 22px;
  outline: 0;

  ${({ selected, theme }) => selected ? `
    position: relative;
    & > span {
      color: ${theme.highlight};
      font-weight bold;
    }
  ` : ''}

  ${({ selected }) => !selected ? `
    &:hover {
      filter: brightness(0.98);
      z-index: ${zIndexScale.tooltip};
    }
  ` : ''}

  &:not(:first-child) {
    margin-left: -1px;
  }
`;

const FormsFlexBox = styled(BorderedFlexBox)`
  background: ${({ theme }) => theme.formsbg};
  margin: -1px 0 0 0;
`;

const StopLimitSpan = styled.span`
  color: ${({ theme }) => theme.highlight};

  &:hover {
    color: ${({ theme }) => theme.highlight};
  }
`;

const HeaderFlexBox = styled(FlexBox)`
  background:${({ theme }) => theme.formsHeaderbg};
`;

type Props = {
  i18n: Object,
  locale: string,
  base: string,
  pair: Array<any>,
  omitFee?: boolean,
};

type State = { selectedOrderType: string };

export class TabbedOrderForms extends React.PureComponent<Props, State> {
  static defaultProps = {
    omitFee: false,
  }

  state = { selectedOrderType: 'limit' }

  _onTabChange = (ev: MouseEvent) => {
    if (!(ev.currentTarget instanceof HTMLElement)) return;
    const { dataset } = ev.currentTarget;
    const { name: clickedOrderType } = dataset;
    this.setState({ selectedOrderType: clickedOrderType });
  }

  render() {
    const {
      i18n,
      base,
      pair,
      omitFee,
      locale,
    } = this.props;
    const { selectedOrderType } = this.state;
    const stopLimitUrl = urlConfig.tooltip.stopLimit.replace('%locale', getSupportLocale(locale));
    const ocoUrl = urlConfig.tooltip.ocoUrl.replace('%locale', getSupportLocale(locale));
    const stopLimitTipContent =
      <React.Fragment>
        <p>
          {/* {i18n.stopLimitInfo || 'A Stop-Limit order is an order to buy or sell a coin once the price reaches a specified price.'} */}
          {getLabel('exchange-trade-stopLimitInfo', i18n)}
        </p>
        {/*
            TODO: Use Localized links. There are 3 versions now,
                  but the locales used are different from we set before.
         */}
        <p>
          <a
            href={stopLimitUrl}
            target="_blank"
            rel="noopener noreferrer"
            id="TabbedOrderFormsMargin-a-exchangeStopLimitViewMore"
          >
            <StopLimitSpan>{getLabel('exchange-trade-viewMore', i18n)}</StopLimitSpan>
          </a>
        </p>
      </React.Fragment>;

    const ocoTipContent =
      <React.Fragment>
        <p>
          {/* {i18n.stopLimitInfo || 'A Stop-Limit order is an order to buy or sell a coin once the price reaches a specified price.'} */}
          {getLabel('exchange-trade-ocoInfo', i18n)}
        </p>
        {/*
            TODO: Use Localized links. There are 3 versions now,
                  but the locales used are different from we set before.
         */}
        <p>
          <a
            href={ocoUrl}
            target="_blank"
            rel="noopener noreferrer"
            id="TabbedOrderFormsMargin-a-exchangeOCOViewMore"
          >
            <StopLimitSpan>{getLabel('exchange-trade-viewMore', i18n)}</StopLimitSpan>
          </a>
        </p>
      </React.Fragment>;


    return (
      <Wrapper direction="column" {...this.props}>
        <HeaderFlexBox justify="space-between">
          <Tabs>
            <Tab
              data-name="limit"
              selected={selectedOrderType === 'limit'}
              onClick={this._onTabChange}
            >
              <span>
                {getLabel('exchange-trade-LimitOrder', i18n)}
              </span>
            </Tab>
            <Tab
              data-name="market"
              selected={selectedOrderType === 'market'}
              onClick={this._onTabChange}
            >
              <span>
                {getLabel('exchange-trade-MarketOrder', i18n)}
              </span>
            </Tab>
            <Tab
              data-name="stop-limit"
              selected={selectedOrderType === 'stop-limit'}
              onClick={this._onTabChange}
            >
              <span>
                {getLabel('exchange-trade-StopLimit', i18n)}
                <Tooltip>{stopLimitTipContent}</Tooltip>
              </span>
            </Tab>
            <Tab
              data-name="oco"
              selected={selectedOrderType === 'oco'}
              onClick={this._onTabChange}
            >
              <span>
                {getLabel('exchange-trade-OCO', i18n)}
                &nbsp;
                <Tooltip>{ocoTipContent}</Tooltip>
              </span>
            </Tab>
          </Tabs>
          <FlexBox>
            <TradeRuleTip pair={pair} />
            { omitFee ? null : <Fee /> }
          </FlexBox>
        </HeaderFlexBox>
        <FormsFlexBox direction="row" flex="1">
          <InnerOrderForms
            i18n={i18n}
            base={base}
            pair={pair}
            orderType={selectedOrderType}
          />
        </FormsFlexBox>
      </Wrapper>);
  }
}

export default TabbedOrderForms;
